import { Component, OnInit, OnDestroy, Input, ViewChild, AfterViewInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Subject } from 'rxjs';
import { Domain } from '@monsido/ng2/modules/models/api/domain';
import { domainModelUtils } from '@monsido/modules/models/api/domain-model-utils/domain-model.utils';
import { DialogCloseValidationPayload, MonDialog } from '@client/app/modules/dialog-upgrade-wrapper/mon-dialog.service';
import { UserRepoService } from '@client/app/services/api/user-repo/user-repo.service';
import { DomainRepoService } from '@client/app/services/api/domain-repo/domain-repo.service';
import { SmartScrollService } from '@client/app/plugins/scroll/smart-scroll.service';
import { DomainService } from '@client/app/services/entities/domain/domain.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { CoreDomainService } from '@client/app/core/services/domain/core-domain.service';
import { TotalDomainsService } from '@client/app/services/total-domains/total-domains.service';
import moment from 'moment';

import { safeClone } from '@client/app/shared/utils/object-utils';
@Component({
    selector: 'form-domain',
    templateUrl: './form-domain.component.html',
    standalone: false,
})
export class FormDomainComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() domain!: Domain;
    @Input() monDialogId?: string;
    @Input() setForm?: (data: { id: string, form: NgForm }) => void;
    @Input() setCloseValidation?: (payload: DialogCloseValidationPayload) => void;

    @ViewChild('domainForm') domainForm!: NgForm;

    private destroy$ = new Subject<void>();

    isCMSValid = true;
    isScanValid = true;
    isSaving = false;
    loading = true;
    users: any[] = [];
    originalDomain: Domain = {} as Domain;
    pageAssistSettings: any = {};
    progress: any = false;
    offset = 0;

    constructor (
        private monDialog: MonDialog,
        private userRepo: UserRepoService,
        private domainRepoService: DomainRepoService,
        private smartScroll: SmartScrollService,
        private domainService: DomainService,
        private activeFeatureService: ActiveFeatureService,
        private coreDomainService: CoreDomainService,
        private totalDomainsService: TotalDomainsService,
    ) {}

    ngOnInit () {
        this.activate();
    }

    ngAfterViewInit () {
        if (this.setForm && this.domainForm) {
            this.setForm({ id: this.monDialogId || '', form: this.domainForm });
        }
        if (this.setCloseValidation) {
            this.setCloseValidation({
                dialogId: this.monDialogId || '',
                cb: () => {
                    return !this.domainForm?.touched;
                },
            });
        }
    }

    ngOnDestroy () {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private activate () {
        const promises: any[] = [];
        this.offset = moment().utcOffset(); // Timezone in minutes
        this.isSaving = false;
        this.progress = false;
        this.users = [];
        this.loading = true;
        this.domain = (new Domain(this.domain || {} as any));
        this.originalDomain = {} as Domain;
        this.pageAssistSettings = {};

        promises.push(this.getUsers());
        if (typeof this.domain.id === 'number') {
            promises.push(this.getDomain());
        }

        this.progress = Promise
            .all(promises)
            .then((values: any[]) => {
                this.users = values[0];
                if (values[1] !== null && typeof values[1] === 'object') {
                    this.domain = values[1];
                    this.originalDomain = safeClone(this.domain);
                }
            }, () => {})
            .finally(() => {
                this.loading = false;
            });
    }

    updateScanAuthValidity (value: boolean) {
        this.isScanValid = value;
    }

    getDomain () {
        return this.domainRepoService.get(this.domain?.id as number);
    }

    // Re-fetches domain and assigns it so child components receive updated running_groups flag
    refreshDomain (): void {
        this.getDomain().then((domain: Domain) => {
            this.domain = domain;
        });
    }

    private getUsers () {
        return this.userRepo.getAll({ page_size: 0 });
    }

    showPageAssist () {
        return this.activeFeatureService.isFeatureActive('page_assist', this.domain);
    }

    submit () {
        if (!this.domainForm.valid || !this.isScanValid || !this.domain) {
            return;
        }

        let promise: any;
        const domain: any = safeClone(this.domain);
        this.isSaving = true;

        const { day, time } = domainModelUtils.getScanDayTimeUTC(domain, this.offset);
        domain.scan.day = day;
        domain.scan.time = time;
        domain.link_excludes = this.clearEmptyLinkExcludes(domain.link_excludes);
        domain.path_constraints = this.clearEmptyPathConstraints(domain.path_constraints);
        domain.additional_internal_urls = this.clearEmptyInternalUrls(domain.additional_internal_urls);
        domain.language = !domain.language ? null : domain.language;
        domain.domain_users = this.setDomainUsersParams(domain);
        domain.scan.login = this.coreDomainService.setupScanLogin(domain.scan.login);
        domain.features.accessibility = domain.features.accessibility || null;
        domain.features.clarity_guideline = domain.features.clarity_guideline || null;
        domain.features.readability_test = domain.features.readability_test || null;

        if (!domain.features.page_fix) {
            delete domain.settings.scripts.page_correct.admin_only;
            delete domain.settings.scripts.page_correct.enabled_checks;
        }

        if (Array.isArray(domain.labels)) {
            domain.labels = domain.labels.map((label: any) => label.id);
        }

        if (domain.id) {
            promise = this.domainService.update(domain.id, domain);
        } else {
            promise = this.domainService.create(domain);
        }

        promise
            .then((domainData: any) => {
                if (this.domainForm) {
                    if (this.setCloseValidation) {
                        this.setCloseValidation({
                            dialogId: this.monDialogId || '',
                            cb: () => true,
                        });
                    }
                    this.domainForm.form.markAsPristine();
                }
                if (domainData) {
                    this.totalDomainsService.updateDomain(domainData);
                    this.close();
                }
            }, () => {})
            .finally(() => {
                this.isSaving = false;
            });
    }

    private setDomainUsersParams (domain: any) {
        const domainUsers = safeClone(domain.domain_users);
        domainUsers.forEach((domainUser: any) => {
            const index = this.users.findIndex((user: any) => {
                return user.id === domainUser.user_id;
            });

            if (index > -1) {
                domainUser.visible = this.users[index].customer_admin || domainUser.visible;
                domainUser.send_report = domainUser.visible && domainUser.send_report;
            } else {
                domainUser.visible = false;
                domainUser.send_report = false;
            }

            return domainUser;
        });

        return domainUsers;
    }

    private clearEmptyInternalUrls (internalUrls: any[]) {
        return internalUrls.filter((url: any) => {
            return typeof url.value === 'string' && url.value.trim().length > 0;
        });
    }

    private clearEmptyPathConstraints (constraints: any[]) {
        return constraints.filter((constraint: any) => {
            return constraint.hasOwnProperty('constraint') && constraint.constraint.length > 0;
        });
    }

    private clearEmptyLinkExcludes (excludes: any[]) {
        return excludes.filter((exclude: any) => {
            return exclude.hasOwnProperty('regex') && exclude.regex.length > 0;
        });
    }

    private close () {
        this.monDialog.close(this.monDialogId, this.domain);
    }

    scrollTo (sectionID: string) {
        const container = document.querySelector('.mon-dialog-body-with-footer');
        if (container) {
            this.smartScroll.scrollTo(container, sectionID, 120);
        }
    }
}
